import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { useAgGridClientTableContext } from 'core/states';
import { useJobCustomerActions } from 'jobs/hooks';
import { IJobCustomer } from 'jobs/types';
import { Color, Variant } from 'theme/enums';

import { RemoveCustomersConfirmation } from './RemoveCustomersConfirmation';

interface IProps {
  jobId: number;
  customers: IJobCustomer[];
  buttonElement?: ReactElement;
}

export const RemoveCustomersButton = ({ customers, buttonElement }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { updateRows, deselectAllRows } = useAgGridClientTableContext();
  const { removeJobCustomers, isLoading } = useJobCustomerActions(NotificationPosition.TOP);

  const onRemove = async () => {
    const customerIds = customers.filter((customer) => customer.id).map((customer) => customer.id);

    const { success } = await removeJobCustomers(customerIds);

    if (success) {
      deselectAllRows();
      updateRows({ remove: customers });
    }

    setIsConfirmationOpen(false);
  };

  const onClick = () => {
    setIsConfirmationOpen(true);
  };

  if (buttonElement) {
    return cloneElement(buttonElement, {
      onClick: onClick,
      disabled: !customers?.length || isLoading,
    });
  }

  return (
    <>
      <Button
        text={t('buttonLabelRemove')}
        variant={Variant.SOFT}
        color={Color.DANGER}
        startDecorator={<Trash />}
        onClick={onClick}
        disabled={!customers?.length || isLoading}
      />

      <RemoveCustomersConfirmation
        isOpen={isConfirmationOpen}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
        customers={customers}
      />
    </>
  );
};
